import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Input, Radio, Space, Tag } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import { ICityTaxAuto } from "../../interfaces";
import { formatCpfCnpj, onlyNumbers } from "../../utils/Masks";
import { formatDateStringToDate, formatDateStringToDateAndTime } from "../../utils/Conversores";
import { removeAcento } from "../../utils/RemoveAcentos";
import { useCityTaxesAutoContext } from "../../contexts/CityTaxesAutoContext";
import { useGetColumnSearchPropsData } from "../../hooks/FilterTable/date";
import { ThemeContext } from "../../contexts/ThemeContext";
import TableActionButton from "../../components/TableActionButton";
import { IActiveFilterItem } from "../../components/ActiveFiltersBar";
import { findChunksCpfCnpj, findChunksText, HIGHLIGHT_STYLE } from "../../utils/Highlight";
import useTableColumnFilter from "../../hooks/useTableColumnFilter";

export const payment_modalities: Record<string, string> = {
    COTA_UNICA: "Cota Única",
    PARCELA: "Parcela",
};

export interface ITableData extends ICityTaxAuto {
    key: number;
    app_user_cpfCnpj: string;
    app_user_name: string;
    documento: 'CPF' | 'CNPJ';
}

export interface ICityTaxesAutoFilters {
    name: string | null;
    cpfCnpj: string | null;
    documento: 'CPF' | 'CNPJ' | null;
    property_code: string | null;
    payment_method: string | null;
    installment_number: string | null;
    payment_date: string | null;
    created_at: string | null;
}

export type SortField =
    | 'app_user_name'
    | 'property_code'
    | 'payment_method'
    | 'installment_number'
    | 'payment_date'
    | 'created_at'
    | null;

export default function useCityTaxesAuto() {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === 'dark';
    const navigate = useNavigate();
    const { cityTaxesAuto, loadCityTaxesAuto } = useCityTaxesAutoContext();
    const { getColumnSearchProps } = useTableColumnFilter();

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [rawData, setRawData] = useState<Array<ITableData>>([]);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 50 });

    const [filters, setFilters] = useState<ICityTaxesAutoFilters>({
        name: null,
        cpfCnpj: null,
        documento: null,
        property_code: null,
        payment_method: null,
        installment_number: null,
        payment_date: null,
        created_at: null,
    });

    const [sorter, setSorter] = useState<{ field: SortField; order: 'asc' | 'desc' | null }>({
        field: 'created_at',
        order: 'desc',
    });

    const [searchName, setSearchName] = useState(filters.name || '');
    const [searchCpfCnpj, setSearchCpfCnpj] = useState(filters.cpfCnpj || '');
    const [searchPropertyCode, setSearchPropertyCode] = useState(filters.property_code || '');

    const refFuncaoLimparFiltrosString = useRef<any>(null);
    const nameInputRef = useRef<any>(null);
    const cpfCnpjInputRef = useRef<any>(null);

    useEffect(() => {
        setSearchName(filters.name || '');
    }, [filters.name]);

    useEffect(() => {
        setSearchCpfCnpj(filters.cpfCnpj || '');
    }, [filters.cpfCnpj]);

    useEffect(() => {
        setSearchPropertyCode(filters.property_code || '');
    }, [filters.property_code]);

    const handleFilterChange = useCallback((key: keyof ICityTaxesAutoFilters, value: any) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
        setPagination((prev) => ({ ...prev, current: 1 }));
    }, []);

    const handleSearchContribuinte = useCallback((confirm?: () => void) => {
        setFilters((prev) => ({
            ...prev,
            name: searchName.trim() || null,
            cpfCnpj: searchCpfCnpj.trim() || null,
        }));
        setPagination((prev) => ({ ...prev, current: 1 }));
        if (confirm) confirm();
    }, [searchName, searchCpfCnpj]);

    const handleClearContribuinte = useCallback((clearFilters?: () => void, confirm?: () => void) => {
        setSearchName('');
        setSearchCpfCnpj('');
        setFilters((prev) => ({
            ...prev,
            name: null,
            cpfCnpj: null,
            documento: null,
        }));
        if (clearFilters) clearFilters();
        if (confirm) confirm();
        setPagination((prev) => ({ ...prev, current: 1 }));
    }, []);

    const handleClearAllFilters = useCallback(() => {
        setFilters({
            name: null,
            cpfCnpj: null,
            documento: null,
            property_code: null,
            payment_method: null,
            installment_number: null,
            payment_date: null,
            created_at: null,
        });
        setSearchName('');
        setSearchCpfCnpj('');
        setSearchPropertyCode('');
        refFuncaoLimparFiltrosString.current?.limparFiltros?.();
        setPagination((prev) => ({ ...prev, current: 1 }));
    }, []);

    const filteredTableData = useMemo(() => {
        let result = rawData;

        if (filters.name) {
            const query = removeAcento(filters.name.trim().toLowerCase());
            result = result.filter((item) =>
                item.app_user_name ? removeAcento(item.app_user_name.toLowerCase()).includes(query) : false
            );
        }

        if (filters.cpfCnpj) {
            const query = onlyNumbers(filters.cpfCnpj);
            result = result.filter((item) => {
                const raw = onlyNumbers(item.app_user_cpfCnpj || '');
                return raw.includes(query);
            });
        }

        if (filters.documento) {
            result = result.filter((item) => item.documento === filters.documento);
        }

        if (filters.property_code) {
            const query = filters.property_code.trim();
            result = result.filter((item) => String(item.property_code || '').includes(query));
        }

        if (filters.payment_method) {
            result = result.filter((item) => item.payment_method === filters.payment_method);
        }

        if (filters.installment_number) {
            const query = filters.installment_number.trim();
            result = result.filter((item) => String(item.installment_number ?? '') === query);
        }

        if (filters.payment_date) {
            const query = filters.payment_date.trim();
            result = result.filter((item) => {
                if (!item.payment_date) return false;
                const formatted = formatDateStringToDate(item.payment_date);
                return formatted.includes(query);
            });
        }

        if (filters.created_at) {
            const query = filters.created_at.trim();
            result = result.filter((item) => {
                if (!item.created_at) return false;
                const formatted = formatDateStringToDate(item.created_at);
                return formatted.includes(query);
            });
        }

        if (sorter.field && sorter.order) {
            result = [...result].sort((a, b) => {
                if (sorter.field === 'app_user_name') {
                    const comp = (a.app_user_name || '').localeCompare(b.app_user_name || '');
                    return sorter.order === 'asc' ? comp : -comp;
                }
                if (sorter.field === 'property_code') {
                    const valA = a.property_code || 0;
                    const valB = b.property_code || 0;
                    return sorter.order === 'asc' ? valA - valB : valB - valA;
                }
                if (sorter.field === 'payment_method') {
                    const comp = (a.payment_method || '').localeCompare(b.payment_method || '');
                    return sorter.order === 'asc' ? comp : -comp;
                }
                if (sorter.field === 'installment_number') {
                    const valA = a.installment_number || 0;
                    const valB = b.installment_number || 0;
                    return sorter.order === 'asc' ? valA - valB : valB - valA;
                }
                if (sorter.field === 'payment_date') {
                    const valA = new Date(a.payment_date || 0).getTime();
                    const valB = new Date(b.payment_date || 0).getTime();
                    return sorter.order === 'asc' ? valA - valB : valB - valA;
                }
                if (sorter.field === 'created_at') {
                    const valA = new Date(a.created_at || 0).getTime();
                    const valB = new Date(b.created_at || 0).getTime();
                    return sorter.order === 'asc' ? valA - valB : valB - valA;
                }
                return 0;
            });
        }

        return result;
    }, [rawData, filters, sorter]);

    const stats = useMemo(() => {
        const total = rawData.length;
        const iptu = rawData.filter((i) => i.type === 'IPTU').length;
        const cotaUnica = rawData.filter((i) => i.payment_method === 'COTA_UNICA').length;
        const parcelado = rawData.filter((i) => i.payment_method === 'PARCELA').length;

        return { total, iptu, cotaUnica, parcelado };
    }, [rawData]);

    const activeFilterItems: Array<IActiveFilterItem | null> = useMemo(() => [
        filters.name
            ? {
                key: 'name',
                label: 'Nome',
                value: filters.name,
                onClear: () => {
                    setSearchName('');
                    handleFilterChange('name', null);
                },
            }
            : null,
        filters.cpfCnpj
            ? {
                key: 'cpfCnpj',
                label: 'CPF/CNPJ',
                value: formatCpfCnpj(filters.cpfCnpj),
                onClear: () => {
                    setSearchCpfCnpj('');
                    handleFilterChange('cpfCnpj', null);
                },
            }
            : null,
        filters.documento
            ? {
                key: 'documento',
                label: 'Tipo Doc.',
                value: filters.documento === 'CPF' ? 'Pessoa Física (CPF)' : 'Pessoa Jurídica (CNPJ)',
                onClear: () => handleFilterChange('documento', null),
            }
            : null,
        filters.property_code
            ? {
                key: 'property_code',
                label: 'Cód. Imóvel',
                value: filters.property_code,
                onClear: () => {
                    setSearchPropertyCode('');
                    handleFilterChange('property_code', null);
                },
            }
            : null,
        filters.payment_method
            ? {
                key: 'payment_method',
                label: 'Modalidade',
                value: payment_modalities[filters.payment_method] || filters.payment_method,
                onClear: () => handleFilterChange('payment_method', null),
            }
            : null,
        filters.installment_number
            ? {
                key: 'installment_number',
                label: 'Parcela',
                value: filters.installment_number,
                onClear: () => handleFilterChange('installment_number', null),
            }
            : null,
        filters.payment_date
            ? {
                key: 'payment_date',
                label: 'Data do Pagamento',
                value: filters.payment_date,
                onClear: () => handleFilterChange('payment_date', null),
            }
            : null,
        filters.created_at
            ? {
                key: 'created_at',
                label: 'Data de Cadastro',
                value: filters.created_at,
                onClear: () => handleFilterChange('created_at', null),
            }
            : null,
    ], [filters, handleFilterChange]);

    const getSortOrder = (field: SortField) => {
        if (sorter.field === field) {
            return sorter.order === 'asc' ? ('ascend' as const) : ('descend' as const);
        }
        return null;
    };

    const handleTableChange = (newPagination: any, tableFilters: any, newSorter: any) => {
        if (newPagination) {
            setPagination({
                current: newPagination.current || 1,
                pageSize: newPagination.pageSize || 50,
            });
        }

        if (tableFilters) {
            if (tableFilters.payment_date !== undefined) {
                const val = tableFilters.payment_date?.[0] || null;
                setFilters((prev) => ({ ...prev, payment_date: val }));
            }
            if (tableFilters.created_at !== undefined) {
                const val = tableFilters.created_at?.[0] || null;
                setFilters((prev) => ({ ...prev, created_at: val }));
            }
            if (tableFilters.payment_method !== undefined) {
                const val = tableFilters.payment_method?.[0] || null;
                setFilters((prev) => ({ ...prev, payment_method: val }));
            }
        }

        if (newSorter && newSorter.field) {
            setSorter({
                field: newSorter.field as SortField,
                order: newSorter.order === 'ascend' ? 'asc' : newSorter.order === 'descend' ? 'desc' : null,
            });
        }
    };

    const handlePaginationChange = useCallback((page: number, pageSize: number) => {
        setPagination({ current: page, pageSize });
    }, []);

    const columns = [
        {
            title: 'Contribuinte',
            dataIndex: 'app_user_name',
            key: 'app_user_name',
            sorter: true,
            sortOrder: getSortOrder('app_user_name'),
            showSorterTooltip: false,
            filterDropdown: ({ confirm, clearFilters }: any) => (
                <div style={{ padding: 12, width: 280, display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: isDark ? '#ccc' : '#475569', marginBottom: 4 }}>
                            Buscar por Nome
                        </div>
                        <Input
                            ref={nameInputRef}
                            placeholder="Digite o nome..."
                            value={searchName}
                            onChange={(e) => setSearchName(e.target.value)}
                            onPressEnter={() => handleSearchContribuinte(confirm)}
                            allowClear
                        />
                    </div>

                    <div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: isDark ? '#ccc' : '#475569', marginBottom: 4 }}>
                            Buscar por CPF ou CNPJ
                        </div>
                        <Input
                            ref={cpfCnpjInputRef}
                            placeholder="000.000.000-00 ou CNPJ"
                            value={searchCpfCnpj}
                            onChange={(e) => setSearchCpfCnpj(formatCpfCnpj(e.target.value))}
                            onPressEnter={() => handleSearchContribuinte(confirm)}
                            allowClear
                        />
                    </div>

                    <div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: isDark ? '#ccc' : '#475569', marginBottom: 4 }}>
                            Tipo de Documento
                        </div>
                        <Radio.Group
                            value={filters.documento || 'ALL'}
                            onChange={(e) => {
                                const val = e.target.value === 'ALL' ? null : e.target.value;
                                handleFilterChange('documento', val);
                            }}
                            size="small"
                        >
                            <Radio value="ALL">Todos</Radio>
                            <Radio value="CPF">CPF</Radio>
                            <Radio value="CNPJ">CNPJ</Radio>
                        </Radio.Group>
                    </div>

                    <Space style={{ justifyContent: 'space-between', marginTop: 4 }}>
                        <Button
                            type="primary"
                            icon={<SearchOutlined />}
                            size="small"
                            onClick={() => handleSearchContribuinte(confirm)}
                            style={{ minWidth: 100 }}
                        >
                            Pesquisar
                        </Button>
                        <Button
                            size="small"
                            onClick={() => handleClearContribuinte(clearFilters, confirm)}
                            style={{ minWidth: 80 }}
                        >
                            Limpar
                        </Button>
                    </Space>
                </div>
            ),
            filterIcon: () => (
                <SearchOutlined style={{ color: (filters.name || filters.cpfCnpj || filters.documento) ? '#1677ff' : undefined }} />
            ),
            onFilterDropdownOpenChange: (visible: boolean) => {
                if (visible) {
                    setTimeout(() => nameInputRef.current?.focus(), 100);
                }
            },
            render: (_: any, record: ITableData) => (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'nowrap' }}>
                        <span style={{ fontWeight: 600, color: isDark ? '#f0f0f0' : '#1e293b', fontSize: 13, lineHeight: '1.3' }}>
                            {filters.name ? (
                                <Highlighter
                                    highlightStyle={HIGHLIGHT_STYLE}
                                    searchWords={[filters.name]}
                                    findChunks={findChunksText}
                                    autoEscape
                                    textToHighlight={record.app_user_name || '--'}
                                />
                            ) : (
                                record.app_user_name || '--'
                            )}
                        </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ color: isDark ? '#8c8c8c' : '#64748b', fontSize: 12, fontWeight: 500, letterSpacing: '0.3px', whiteSpace: 'nowrap' }}>
                            {filters.cpfCnpj ? (
                                <Highlighter
                                    highlightStyle={HIGHLIGHT_STYLE}
                                    searchWords={[filters.cpfCnpj]}
                                    findChunks={findChunksCpfCnpj}
                                    autoEscape
                                    textToHighlight={record.app_user_cpfCnpj ? formatCpfCnpj(record.app_user_cpfCnpj) : '--'}
                                />
                            ) : (
                                record.app_user_cpfCnpj ? formatCpfCnpj(record.app_user_cpfCnpj) : '--'
                            )}
                        </span>
                    </div>
                </div>
            ),
            width: '28%',
        },
        {
            title: 'Cód. Imóvel',
            dataIndex: 'property_code',
            key: 'property_code',
            align: 'center' as const,
            sorter: true,
            sortOrder: getSortOrder('property_code'),
            showSorterTooltip: false,
            ...getColumnSearchProps({
                title: 'Filtrar por Cód. Imóvel',
                placeholder: 'Ex: 12345',
                value: searchPropertyCode,
                onChange: setSearchPropertyCode,
                onSearch: (val) => handleFilterChange('property_code', val),
                onClear: () => handleFilterChange('property_code', null),
                isActive: Boolean(filters.property_code),
            }),
            render: (code: number) => (
                <Tag color="cyan" style={{ borderRadius: 10, fontWeight: 600, fontSize: 12, margin: 0, padding: '1px 8px' }}>
                    {code ?? '--'}
                </Tag>
            ),
            width: '12%',
        },
        {
            title: 'Modalidade',
            dataIndex: 'payment_method',
            key: 'payment_method',
            align: 'center' as const,
            sorter: true,
            sortOrder: getSortOrder('payment_method'),
            showSorterTooltip: false,
            filters: [
                { text: 'Cota Única', value: 'COTA_UNICA' },
                { text: 'Parcela', value: 'PARCELA' },
            ],
            filteredValue: filters.payment_method ? [filters.payment_method] : null,
            render: (method: 'COTA_UNICA' | 'PARCELA') => (
                <Tag
                    color={method === 'COTA_UNICA' ? 'green' : 'orange'}
                    style={{ borderRadius: 10, fontWeight: 500, fontSize: 11, margin: 0 }}
                >
                    {payment_modalities[method] || method || '--'}
                </Tag>
            ),
            width: '12%',
        },
        {
            title: 'Parcela',
            dataIndex: 'installment_number',
            key: 'installment_number',
            align: 'center' as const,
            sorter: true,
            sortOrder: getSortOrder('installment_number'),
            showSorterTooltip: false,
            render: (installmentNumber?: number) => (
                <span style={{ color: isDark ? '#a6a6a6' : '#64748b', fontWeight: 500, fontSize: 13 }}>
                    {installmentNumber !== undefined && installmentNumber !== null ? installmentNumber : '--'}
                </span>
            ),
            width: '8%',
        },
        {
            title: 'Data do Pagamento',
            dataIndex: 'payment_date',
            key: 'payment_date',
            align: 'center' as const,
            sorter: true,
            sortOrder: getSortOrder('payment_date'),
            showSorterTooltip: false,
            ...useGetColumnSearchPropsData({
                dataIndex: 'payment_date',
                ref: refFuncaoLimparFiltrosString,
                onFilterClear: () => handleFilterChange('payment_date', null),
                initialValue: filters.payment_date,
            }),
            render: (data_pagamento: any) => (
                <span style={{ color: isDark ? '#f0f0f0' : '#1e293b', fontSize: 13, whiteSpace: 'nowrap' }}>
                    {data_pagamento ? formatDateStringToDate(data_pagamento) : '--'}
                </span>
            ),
            width: '15%',
        },
        {
            title: 'Data de Cadastro',
            dataIndex: 'created_at',
            key: 'created_at',
            align: 'center' as const,
            sorter: true,
            sortOrder: getSortOrder('created_at'),
            showSorterTooltip: false,
            ...useGetColumnSearchPropsData({
                dataIndex: 'created_at',
                ref: refFuncaoLimparFiltrosString,
                onFilterClear: () => handleFilterChange('created_at', null),
                initialValue: filters.created_at,
            }),
            render: (criado: any) => (
                <span style={{ color: isDark ? '#8c8c8c' : '#64748b', fontSize: 13, whiteSpace: 'nowrap' }}>
                    {criado ? formatDateStringToDateAndTime(criado, true) : '--'}
                </span>
            ),
            width: '15%',
        },
        {
            title: 'Ações',
            key: 'actions',
            align: 'center' as const,
            width: '8%',
            render: (_: any, record: ITableData) => (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                    <TableActionButton
                        action="view"
                        tooltip="Visualizar usuário"
                        onClick={(e) => {
                            e?.stopPropagation?.();
                            if (record.app_user?.id) {
                                navigate(`/usuarios/auto/${record.app_user.id}`);
                            }
                        }}
                    />
                </div>
            ),
        },
    ];

    const setTableDataFromRequest = useCallback((data: Array<ICityTaxAuto>) => {
        const newData: Array<ITableData> = (data || []).map((tax) => {
            const rawCpfCnpj = tax.app_user?.cpfCnpj || '';
            const isCnpj = onlyNumbers(rawCpfCnpj).length > 11;

            return {
                ...tax,
                key: tax.id,
                app_user_cpfCnpj: rawCpfCnpj,
                app_user_name: tax.app_user?.name || '',
                documento: isCnpj ? 'CNPJ' : 'CPF',
            };
        });
        setRawData(newData);
    }, []);

    useEffect(() => {
        setTableDataFromRequest(cityTaxesAuto);
    }, [cityTaxesAuto, setTableDataFromRequest]);

    useEffect(() => {
        const controller = new AbortController();

        async function loadData() {
            setIsLoading(true);
            await loadCityTaxesAuto(controller);
            setIsLoading(false);
        }

        loadData();

        return () => {
            controller.abort();
        };
    }, [loadCityTaxesAuto]);

    const handleClickImportCityTaxAuto = useCallback(() => {
        navigate(`/impostos/auto/importacao`);
    }, [navigate]);

    return {
        isLoading,
        columns,
        tableData: filteredTableData,
        stats,
        pagination,
        activeFilterItems,
        handleTableChange,
        handlePaginationChange,
        handleClearAllFilters,
        handleClickImportCityTaxAuto,
        navigate,
    };
}
